/* eslint-disable @typescript-eslint/no-non-null-assertion */
import React from 'react'
import { Routes, Route, Outlet, useMatch } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { GoogleReCaptchaProvider } from 'react-google-recaptcha-v3'

import { APP_ROUTES, KEYCLOAK_ROLES } from 'helpers/constants'
import SignUp from 'components/SignUp'
import { useDocumentTitle } from 'hooks/useDocumentTitle'
import RequireAuth from 'components/access/RequireAuth'
import RequireNonAuth from 'components/access/RequireNonAuth'
import RequireAdmin from 'components/access/RequireAdmin'
import RequireAgent from 'components/access/RequireAgent'
import RequireRole from 'components/access/RequireRole'
import KeycloakService from 'services/KeycloakService'
import NotFound from 'components/NotFound'
import Dashboard from 'components/Dashboard'
import Agents from 'components/Agents'
import AgentCreateEdit from 'components/CreateAgent'
import CreatePropertyAd from 'components/CreatePropertyAd'
import AgentProperties from 'components/AgentProperties'
import Lists from 'components/Lists'
import List from 'components/List'
import PropertyAds from 'components/PropertyAds'
import Landing from 'components/Landing'
import ToolsPage from 'components/CalculatorTools'
import GuestLayout from 'layouts/GuestLayout'
import NotFoundInner from 'components/NotFound'
import Login from 'components/Login/index'
import SignupUser from 'components/SignupUser'
import Users from 'components/Users'
import NotAllowed from 'components/NotAllowed'
import CreateUser from 'components/CreateUser'
import UsersEdit from 'components/UsersEdit'
import ResetPassword from 'components/ResetPassword'
import { merge } from 'lodash'
import { ThemeProvider, createTheme } from '@mui/material/styles'
import { themeBase } from 'theme'

const breakpointsThemeBase = {
  breakpoints: {
    values: {
      xs: 0,
      sm: 699,
      md: 700,
      lg: 901,
      xl: 1440,
    },
  },
}
const breakpointsTheme = createTheme(merge(themeBase, breakpointsThemeBase))

const AppRoutes: React.FC = () => {
  const { t } = useTranslation()
  const { addTitle } = useDocumentTitle()

  const matchLanding = useMatch('/')
  const matchTools = useMatch('/tools')
  const matchCreatePropertyAd = useMatch('/property-ads/create')
  const matchEditPropertyAd = useMatch('/property-ads/:propertyAdId')
  const matchAgentCreate = useMatch('/agents/create')
  const matchAgentEdit = useMatch('/agents/:agentId')
  const matchAgents = useMatch('/agents')
  const matchPropertyAds = useMatch('/property-ads')
  const matchAgentProperties = useMatch('/agent-properties/*')
  const matchMyProperties = useMatch('/my-property-ads')
  const matchMyPropertiesEdit = useMatch('/my-property-ads/:propertyAdId')
  const match404 = useMatch('404')
  const match403 = useMatch('/not-allowed')
  const matchLogin = useMatch('/login')
  const matchSignUpAgent = useMatch('/sign-up')
  const matchSignUpUser = useMatch('/sign-up-user')
  const matchCreateUser = useMatch('/users/new')
  const matchProfile = useMatch('/profile')
  const matchLists = useMatch('/lists')
  const matchListInsideView = useMatch('/lists/:listId')
  const matchResetPassword = useMatch(`/${APP_ROUTES.ROUTE_RESET_PASSWORD}`)

  if (matchLanding) addTitle(t('pages.dashboard'))
  else if (matchTools) addTitle(t('pages.tools'))
  else if (matchCreatePropertyAd) addTitle(t('pages.createPropertyAd'))
  else if (matchEditPropertyAd) addTitle(t('pages.editPropertyAd'))
  else if (matchAgents) addTitle(t('pages.agents'))
  else if (matchAgentCreate) addTitle(t('pages.createAgent'))
  else if (matchAgentEdit) addTitle(t('pages.editAgent'))
  else if (matchPropertyAds) addTitle(t('pages.propertyAds'))
  else if (matchAgentProperties) addTitle(t('pages.agentProperties'))
  else if (matchMyProperties) addTitle(t('pages.myPropertyAds'))
  else if (matchMyPropertiesEdit) addTitle(t('pages.myPropertyAd'))
  else if (match404) addTitle(t('pages.404'))
  else if (match403) addTitle(t('pages.403'))
  else if (matchLogin) addTitle(t('pages.login'))
  else if (matchSignUpAgent) addTitle(t('pages.signup'))
  else if (matchSignUpUser) addTitle(t('pages.signUpUser'))
  else if (matchCreateUser) addTitle(t('pages.createUser'))
  else if (matchProfile) addTitle(t('pages.profile'))
  else if (matchLists) addTitle(t('pages.lists'))
  else if (matchListInsideView) addTitle(t('pages.listDetailView'))
  else if (matchResetPassword) addTitle(t('pages.resetPassword'))

  return (
    <Routes>
      <Route
        element={
          <RequireAuth>
            <Dashboard />
          </RequireAuth>
        }
      >
        <Route index element={<Landing />} />

        <Route
          path="property-ads"
          element={
            <RequireAdmin>
              <PropertyAds />
            </RequireAdmin>
          }
        />

        <Route path="property-ads">
          <Route
            path="create"
            element={
              <RequireAgent>
                <CreatePropertyAd key={window.location.href} />
              </RequireAgent>
            }
          />
          <Route
            path=":propertyAdId"
            element={
              <RequireRole
                roles={[KEYCLOAK_ROLES.ROLE_ADMIN, KEYCLOAK_ROLES.ROLE_AGENT]}
              >
                <CreatePropertyAd key={window.location.href} />
              </RequireRole>
            }
          />
        </Route>

        <Route path="lists">
          <Route
            index
            element={
              <ThemeProvider theme={breakpointsTheme}>
                <Lists />
              </ThemeProvider>
            }
          />
          <Route
            path=":listId"
            element={
              <ThemeProvider theme={breakpointsTheme}>
                <List />
              </ThemeProvider>
            }
          />
        </Route>

        <Route
          path="tools"
          element={
            <RequireAdmin>
              <ToolsPage />
            </RequireAdmin>
          }
        />
        <Route
          path="agents"
          element={
            <RequireAdmin>
              <Agents />
            </RequireAdmin>
          }
        />
        <Route
          path="my-property-ads"
          element={
            <RequireAgent>
              <AgentProperties />
            </RequireAgent>
          }
        />
        <Route
          path="my-property-ads/:propertyAdId"
          element={
            <RequireAgent>
              <CreatePropertyAd />
            </RequireAgent>
          }
        />
        <Route
          path="agent-properties"
          element={
            <RequireAdmin>
              <Outlet />
            </RequireAdmin>
          }
        >
          <Route path=":agentId" element={<AgentProperties />} />
        </Route>
        <Route
          path="agents"
          element={
            <GoogleReCaptchaProvider
              reCaptchaKey={process.env.REACT_APP_GOOGLE_RECAPTCHA_KEY}
            >
              <RequireAdmin>
                <Outlet />
              </RequireAdmin>
            </GoogleReCaptchaProvider>
          }
        >
          <Route path="create" element={<AgentCreateEdit />} />
          <Route path=":agentId" element={<AgentCreateEdit />} />
        </Route>
        <Route
          path="users"
          element={
            <RequireAdmin>
              <Users />
            </RequireAdmin>
          }
        />
        <Route
          path="users/:userId"
          element={
            <GoogleReCaptchaProvider
              reCaptchaKey={process.env.REACT_APP_GOOGLE_RECAPTCHA_KEY}
            >
              <RequireAdmin>
                <UsersEdit />
              </RequireAdmin>
            </GoogleReCaptchaProvider>
          }
        />
        <Route
          path="users/new"
          element={
            <GoogleReCaptchaProvider
              reCaptchaKey={process.env.REACT_APP_GOOGLE_RECAPTCHA_KEY}
            >
              <RequireAdmin>
                <CreateUser />
              </RequireAdmin>
            </GoogleReCaptchaProvider>
          }
        />
        <Route
          path="profile"
          element={
            <RequireRole
              roles={[KEYCLOAK_ROLES.ROLE_USER, KEYCLOAK_ROLES.ROLE_AGENT]}
            >
              {KeycloakService.isAgent() ? <AgentCreateEdit /> : <UsersEdit />}
            </RequireRole>
          }
        />

        <Route path="not-allowed" element={<NotAllowed />} />
        <Route path="*" element={<NotFoundInner />} />
      </Route>

      <Route
        path="sign-up"
        element={
          <RequireNonAuth>
            <GuestLayout>
              <GoogleReCaptchaProvider
                reCaptchaKey={process.env.REACT_APP_GOOGLE_RECAPTCHA_KEY}
              >
                <SignUp />
              </GoogleReCaptchaProvider>
            </GuestLayout>
          </RequireNonAuth>
        }
      />
      <Route
        path="sign-up-user"
        element={
          <RequireNonAuth>
            <GuestLayout>
              <GoogleReCaptchaProvider
                reCaptchaKey={process.env.REACT_APP_GOOGLE_RECAPTCHA_KEY}
              >
                <SignupUser />
              </GoogleReCaptchaProvider>
            </GuestLayout>
          </RequireNonAuth>
        }
      />

      <Route
        path="login"
        element={
          <RequireNonAuth>
            <GuestLayout>
              <GoogleReCaptchaProvider
                reCaptchaKey={process.env.REACT_APP_GOOGLE_RECAPTCHA_KEY}
              >
                <Login />
              </GoogleReCaptchaProvider>
            </GuestLayout>
          </RequireNonAuth>
        }
      />

      <Route
        path={APP_ROUTES.ROUTE_RESET_PASSWORD}
        element={
          <GuestLayout>
            <GoogleReCaptchaProvider
              reCaptchaKey={process.env.REACT_APP_GOOGLE_RECAPTCHA_KEY}
            >
              <ResetPassword />
            </GoogleReCaptchaProvider>
          </GuestLayout>
        }
      ></Route>
      <Route path="404" element={<NotFound />} />
      <Route path="*" element={<NotFound />} />
    </Routes>
  )
}

export default AppRoutes
